import { inject, Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { EditorAssistance } from '../../../../models/EditorAssistance'
import { AssistanceEditorModalService } from '../assistance-editor-modal.service'
import { AssistanceEditorService } from '../../assistance-editor/assistance-editor.service'
import { AssistanceService } from '../../../../services/domain/assistance/assistance.service'
import { PrimeNgToastService } from '../../../../services/prime-ng-toast/prime-ng-toast.service'

/**
 * A group session is a bundle of single-client assistances. The bundle parent carries the first client plus all
 * shared assistants; each additional client is a child assistance with no assignments. This service adds/removes
 * those client members; assistants and status are handled on the backend (assignments route to the parent, status
 * is synced to children).
 */
@Injectable({ providedIn: 'root' })
export class GroupSessionService {
  private modalService = inject(AssistanceEditorModalService)
  private editorService = inject(AssistanceEditorService)
  private assistanceService = inject(AssistanceService)
  private toaster = inject(PrimeNgToastService)

  /**
   * Adds a client to the group: creates a new bundle child of the main assistance and assigns the client to it.
   * Returns the real (materialized) parent id — the caller must reload by it, not by `main`'s own URID, which may
   * still be an unmaterialized recurrence occurrence at call time. Returns null if the client already has an
   * overlapping assistance elsewhere and the add was blocked.
   */
  async addClient(main: EditorAssistance, clientId: number): Promise<number | null> {
    const overlaps = await firstValueFrom(
      this.assistanceService.isOverlappingAnyAssistanceForTheSameClient(
        main.getDateFrom(),
        main.getDateTo(),
        clientId,
        null,
        { getDateFrom: () => main.getDateFrom(), getDateTo: () => main.getDateTo(), recurrenceParentId: null, bundleParentId: main.id },
        null,
      ),
    )
    if (overlaps.length > 0) {
      this.toaster.displayCustomToast('error', 'GROUP_SESSION_EDITOR.ADD_CLIENT_OVERLAP_BLOCKED')
      return null
    }

    const child = await this.modalService.createBundleChild(main, true)
    await this.editorService.patch({ urid: child.id.toString(), clientId })
    return child.bundleParentId!
  }

  /**
   * Removes a client member from the group session bundle.
   * @returns true if the member was removed, false if removal was blocked.
   */
  async removeMember(member: EditorAssistance): Promise<boolean> {
    try {
      await this.editorService.remove(member)
      return true
    } catch (error) {
      // The backend only blocks removal (Conflict) when the primary still has assistants assigned — any other
      // error is unrelated to that guard and should surface as a generic failure, not this specific message.
      if (!(error instanceof Error) || error.name !== 'Conflict') throw error
      this.toaster.displayCustomToast('error', 'GROUP_SESSION_EDITOR.REMOVE_PRIMARY_BLOCKED')
      return false
    }
  }

  /** The primary member is the bundle parent (no bundleParentId). */
  isPrimaryMember(member: EditorAssistance): boolean {
    return !member.bundleParentId
  }
}
